<template>
  <ValidationProvider
    tag="div"
    :rules="rulesObject"
    v-show="show"
    v-slot="{ errors }"
    :class="{
      'f-col-span-2': true,
      'sm:f-col-span-1': !singleColumn,
    }"
  >
    <BaseInputGroup
      :for="$id('country')"
      :label="$t('basic_info.country.label') + '*'"
      :error="getFirstError(errors)"
      :single-column="singleColumn"
    >
      <template slot="input">
        <div class="f-relative" v-click-outside="closeDropdown">
          <input
            type="text"
            tabindex="-1"
            class="f-form-input f-absolute f-py-1 f-bg-transparent f-text-transparent f-select-none f-block f-w-full f-border-gray-300 focus:f-outline-none f-rounded-md country-fake-input"
            :class="{
              'f-border-red-300 focus:f-ring-red-500 focus:f-border-red-500 f-text-red-900 f-placeholder-red-300':
                getFirstError(errors),
            }"
            :id="$id('country')"
            :readonly="true"
            v-model="value"
            :name="name"
          />
          <div class="f-p-px">
            <button
              type="button"
              tabindex="0"
              aria-haspopup="listbox"
              aria-expanded="true"
              aria-labelledby="listbox-label"
              class="f-relative f-w-full f-bg-transparent f-rounded-md f-shadow-sm f-px-3 f-pr-6 f-py-1 f-text-left f-cursor-default focus:f-outline-none focus:f-ring-2 focus:f-ring-indigo-500 focus:f-border-0 f-text-gray-700"
              @mouseenter="triggerLoadCountries"
              @click="triggerShowCountries"
            >
              <span class="f-flex f-items-center">
                <img
                  class="f-flex-shrink-0 f-w-5"
                  :src="getImageUrl(value)"
                  :alt="allowedCountries[value]"
                  v-if="value"
                />
                <span class="f-ml-2 f-block f-truncate">
                  {{ allowedCountries[value] }}
                </span>
              </span>
              <span
                class="f-absolute f-inset-y-0 f-right-0 f-flex f-items-center f-pr-1 f-pointer-events-none"
              >
                <svg
                  class="f-h-4 f-w-4 f-text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                    :transform="showOptions ? 'rotate(180 12 12)' : ''"
                  />
                </svg>
              </span>
            </button>
          </div>
          <div v-show="showOptions">
            <template v-if="loadOptions">
              <div
                class="f-absolute f-mt-1 f-rounded-md f-border f--ml-px f-w-full f-bg-white f-text-gray-700"
              >
                <ul
                  ref="listElement"
                  tabindex="-1"
                  role="listbox"
                  aria-labelledby="listbox-label"
                  aria-activedescendant="listbox-item-3"
                  style="max-height: 208px"
                  class="f-rounded-md f-py-1 f-z-10 f-relative f-bg-white f-text-base f-ring-1 f-ring-black f-ring-opacity-5 f-overflow-auto focus:f-outline-none"
                >
                  <li
                    role="option"
                    tabindex="0"
                    class="f-text-gray-900 f-cursor-default f-select-none f-relative f-py-2 f-pl-3 f-pr-9 hover:f-bg-gray-200 f-cursor-pointer"
                    :key="index"
                    :class="{ 'f-bg-gray-300 selected': value === index }"
                    :data-value="index"
                    v-for="(countryName, index) in allowedCountries"
                    @click="countrySelected(index)"
                    @keydown.enter="countrySelected(index)"
                    @keydown.space.prevent="countrySelected(index)"
                  >
                    <div class="f-flex f-items-center">
                      <img
                        :src="getImageUrl(index)"
                        :alt="countryName"
                        class="f-flex-shrink-0 f-w-5"
                        @error="onImageLoadError"
                      />
                      <span
                        class="f-ml-3 f-flex-shrink-0 f-w-max f-font-normal"
                      >
                        {{ countryName }}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>
      </template>
      <template slot="error">
        <p v-if="errors.length" class="f-text-xs f-text-red-600 f-mb-0 f-mt-1">
          {{ getFirstError(errors) }}
        </p>
      </template>
    </BaseInputGroup>
  </ValidationProvider>
</template>

<script lang="ts">
import { ValidationProvider } from "vee-validate";
import FormHelper from "@/module/FormHelper";
import FormResource from "@/module/Form/Resources/FormResource";
import FormData from "@/module/FormData";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import mixins from "vue-typed-mixins";
import { PropType } from "vue";
import BaseInputGroup from "@/components/Form/Basic/Inputs/BaseInputGroup.vue";
// @ts-ignore
import ClickOutside from "vue-click-outside";

interface ComponentData {
  allowedCountries: { [key: string]: string };
  oldValue: string | null;
  showOptions: boolean;
  loadOptions: boolean;
}

export default mixins(InputValidationMixin).extend({
  name: "BaseCountry",

  components: {
    BaseInputGroup,
    ValidationProvider,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    formData: {
      type: Object as PropType<FormData>,
      required: false,
    },
    showInputInFullForm: {
      type: Boolean,
      required: true,
    },
    showInput: {
      type: Boolean,
      required: true,
    },
    isFullForm: {
      type: Boolean,
      required: true,
    },
    required: {
      type: Boolean,
      required: true,
    },
    singleColumn: {
      type: Boolean,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      allowedCountries: {},
      oldValue: null,
      showOptions: false,
      loadOptions: false,
    };
  },

  computed: {
    rulesObject(): object {
      return this.show ? { required: this.required } : {};
    },

    show(): boolean {
      if (this.isFullForm && this.showInputInFullForm) {
        return true;
      }

      return this.showInput;
    },
  },

  methods: {
    closeDropdown() {
      if (this.showOptions) {
        this.showOptions = false;
      }
    },

    triggerLoadCountries(): void {
      this.loadOptions = true;
    },

    triggerShowCountries(): void {
      this.triggerLoadCountries();
      this.showOptions = !this.showOptions;
      const listElement = this.$refs.listElement as HTMLElement | undefined;

      if (listElement) {
        const selected = listElement.querySelector(
          "li[data-value='" + this.value + "']",
        ) as HTMLElement | undefined;

        if (selected) {
          this.$nextTick(() => {
            listElement.scroll({
              top: selected.offsetTop,
            });
          });
        }
      }
    },

    countrySelected(countryCode: string): void {
      this.triggerShowCountries();

      this.update(countryCode);
    },

    update(value: string | null): void {
      if (value !== null) {
        this.value = value;
      }

      if (this.oldValue === value) {
        return;
      }

      if (value === "") {
        value = null;
      }

      this.oldValue = value;
      this.$emit("change", value);
    },

    getImageUrl(countryCode: string): string {
      return (
        this.formResource.baseUrl +
        "/images/flags/" +
        String(countryCode).toUpperCase().split("-")[0] +
        ".svg"
      );
    },

    onImageLoadError(data: Event): void {
      const target = data.target as HTMLImageElement | null;

      if (target === null) {
        return;
      }

      target.src = this.formResource.baseUrl + "/images/flags/unknown.svg";
    },
  },

  watch: {
    show(newShowValue: boolean, oldShowValue: boolean): void {
      if (newShowValue === oldShowValue) {
        return;
      }

      let value = this.value;

      if (!this.show) {
        value = null;
      }

      this.update(value);
    },
    showOptions: function (val) {
      const input = document.querySelector(
        ".country-fake-input",
      ) as HTMLElement;
      if (val) {
        input.focus({ preventScroll: true });
      } else {
        input.blur();
      }
    },
  },

  created(): void {
    const allowedCountries = this.formResource.allowed_countries;

    this.loadDataFromQuery();

    if (this.value === null) {
      var defaultCountryCode = this.formResource.default_country;

      if (
        this.formData !== undefined &&
        this.formData.address.country !== null
      ) {
        defaultCountryCode = this.formData.address.country;
      }

      const defaultCountry = FormHelper.resolveDefaultCountry(
        defaultCountryCode,
        this.formResource.language,
      );

      if (allowedCountries.hasOwnProperty(defaultCountry)) {
        this.value = defaultCountry;
      } else {
        this.value = Object.keys(allowedCountries)[0] ?? null;
      }
    }

    this.allowedCountries = allowedCountries;

    if (this.show) {
      this.update(this.value);
    }
  },

  directives: {
    ClickOutside,
  },
});
</script>

<style lang="scss"></style>
